/* eslint-disable camelcase */
import { d3, cropr, gettext } from "../adapter";
import each from "lodash/each";
import clone from "lodash/clone";
import extend from "lodash/extend";
import min from "lodash/min";
import sortBy from "lodash/sortBy";
import moment from "moment";
import timeFormat from "./chartTimeFormat";
import sensorActions from "../actions/sensorActions";

var bisectDate = d3.bisector(function (d) {
  return d.timestamp;
}).left;

class Chart {
  constructor(options, data, dispatch) {
    // Empty data, this is our data format
    this.data = { values: [] };
    this.precipitationInterval = 0;
    this.dispatch = dispatch;

    // Default options
    this.options = {
      id: false,
      class: false,
      width: 500,
      height: 350,
      isBrush: false,
      hasPrecipitation: false,
      hasTemperature: false,
      temperature_depth: 100,
      interpolation: "cardinal",
      tension: 0.9,
      i18n: {
        unit: cropr.l10n.unit_precipitation_display,
        unitBig: cropr.l10n.unit_precipitation_display,
        unitTemperature: cropr.l10n.unit_temp_display, //'°C',
      },
    };

    // Merge the options
    this.options = extend(this.options, options);

    // Set chart dimension, also depending on settings as brush
    var margin = { top: 10, right: 25, bottom: 30, left: 70 };
    var height = this.options.height - (margin.top + margin.bottom);
    var width = this.options.width - (margin.left + margin.right);
    var margintop = 0;

    bisectDate = d3.bisector(function (d) {
      return d.timestamp;
    }).left;

    if (this.options.hasPrecipitation || this.options.hasTemperature) {
      var betweenChartMargin = 25;
      if (this.options.hasPrecipitation && this.options.hasTemperature) {
        betweenChartMargin = 50;
      }

      if (this.options.hasPrecipitation) {
        var precipitationMargin = clone(margin);
        this.precipitationChart = {
          width: width,
          height: height * (this.options.hasTemperature ? 0.1 : 0.2),
          margin: precipitationMargin,
        };
        margintop = this.precipitationChart.margin.top;
      }

      if (this.options.hasTemperature) {
        this.options.temperature_depth = data.depths[0]; // set temperature to upper sensor
        var temperatureMargin = clone(margin);
        this.temperatureChart = {
          width: width,
          height: height * (this.options.hasPrecipitation ? 0.1 : 0.2),
          margin: temperatureMargin,
        };
        margintop = this.temperatureChart.margin.top;
        if (this.options.hasPrecipitation) {
          this.temperatureChart.margin.top =
            this.temperatureChart.margin.top + height * 0.1 + betweenChartMargin / 2;
        }
      }

      margin.top = margin.top + height * 0.2 + betweenChartMargin;
      this.chart = {
        width: width,
        height: height * 0.8 - betweenChartMargin,
        margin: margin,
      };

      this.totalChart = {
        // The total height of both charts
        height: this.chart.height + this.chart.margin.top - margintop,
        margin: {
          top: margintop,
        },
      };
    } else {
      this.chart = {
        width: width,
        height: height,
        margin: margin,
      };

      this.totalChart = {
        height: this.chart.height,
        margin: {
          top: this.chart.margin.top,
        },
      };
    }

    // Set the data
    if (data) {
      this.setData(data);
    }

    // No SVG and elements yet
    this.elements = {};

    var _this = this;
    var scrolling = false;
    var x;
    this.zoom = d3.behavior
      .zoom()
      .x(this.scales.x)
      .on("zoom", function () {
        if (_this.scales.x.domain()[0] < _this.data.minDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.minDate) +
            _this.scales.x.range()[0];
          _this.zoom.translate([x, 0]);
        } else if (_this.scales.x.domain()[1] > _this.data.maxDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.maxDate) +
            _this.scales.x.range()[1];
          _this.zoom.translate([x, 0]);
        }

        // Execute only once
        if (!scrolling) {
          _this.elements.eventRect.classed("grabbing", true);
          dispatch(sensorActions.hovered(false));
        }

        scrolling = true;
        _this._scroll();
      })
      .on("zoomend", function () {
        if (!scrolling) return;

        _this.elements.eventRect.classed("grabbing", false);

        // Enable the pin hovers again when we stop zooming
        if (this === d3.event.sourceEvent.target) {
          dispatch(sensorActions.hovered(true));
        }

        scrolling = false;
        dispatch(
          sensorActions.brushDomain([_this.scales.x.domain()[0], _this.scales.x.domain()[1]])
        );
      });
  }
}
Chart.prototype.initTemperature = function (el) {
  var temperatureChart = (this.elements.temperatureChart = this.elements.svg
    .append("g")
    .attr("class", "temperature chart")
    .attr(
      "transform",
      "translate(" +
        this.temperatureChart.margin.left +
        "," +
        this.temperatureChart.margin.top +
        ")"
    ));

  this.elements.temperatureInnerChart = this.elements.svg
    .append("g")
    .attr("class", "temperature inner")

    .attr("clip-path", "url(#clip-temperature-" + this.options.id + ")")
    .attr(
      "transform",
      "translate(" +
        this.temperatureChart.margin.left +
        "," +
        this.temperatureChart.margin.top +
        ")"
    );

  this.elements.temperatureVisuals = this.elements.temperatureInnerChart
    .append("g")

    .attr("mask", "url(#mask-" + this.options.id + ")")
    .attr("class", "temperature visuals");

  this.elements.temperatureGrid = {
    x: temperatureChart
      .append("g")
      .attr("class", "temperature grid x")
      .attr("transform", "translate(0," + this.temperatureChart.height + ")"),
    y: temperatureChart.append("g").attr("class", "temperature grid y"),
  };

  // Draw the axis
  this.elements.temperatureAxis = {
    x: temperatureChart
      .append("g")
      .attr("class", "temperature axis x")
      .attr("transform", "translate(0," + this.temperatureChart.height + ")"),
    y: temperatureChart.append("g").attr("class", "temperature axis y"),
  };

  this.elements.temperatureLine = this.elements.temperatureInnerChart
    .append("path")
    .attr("class", "line temperature");
};

Chart.prototype.initPrecipitation = function (el) {
  var precipitationChart = (this.elements.precipitationChart = this.elements.svg
    .append("g")
    .attr("class", "precipitation chart")
    .attr(
      "transform",
      "translate(" +
        this.precipitationChart.margin.left +
        "," +
        this.precipitationChart.margin.top +
        ")"
    ));

  this.elements.precipitationInnerChart = this.elements.svg
    .append("g")
    .attr("class", "precipitation inner")

    .attr("clip-path", "url(#clip-" + this.options.id + ")")
    .attr(
      "transform",
      "translate(" +
        this.precipitationChart.margin.left +
        "," +
        this.precipitationChart.margin.top +
        ")"
    );

  // this.elements.precipitationVisuals = this.elements.precipitationInnerChart.append('g')

  //   .attr('mask', 'url(#mask-' + this.options.id + ')')
  //   .attr('class', 'precipitation visuals');

  this.elements.precipitationGrid = {
    x: precipitationChart
      .append("g")
      .attr("class", "precipitation grid x")
      .attr("transform", "translate(0," + this.precipitationChart.height + ")"),
    y: precipitationChart.append("g").attr("class", "precipitation grid y"),
  };

  // Draw the axis
  this.elements.precipitationAxis = {
    x: precipitationChart
      .append("g")
      .attr("class", "precipitation axis x")
      .attr("transform", "translate(0," + this.precipitationChart.height + ")"),
    y: precipitationChart.append("g").attr("class", "precipitation axis y"),
  };

  // this.elements.precipitationBars = this.elements.precipitationVisuals.selectAll('.bar.stack')
  //   .data(this.data.sensor.measurements.precipitation)
  //   .enter().append('rect').attr('class', 'precipitation bar stack');
};

// Init graph
Chart.prototype.init = function (el) {
  var _this = this;

  // Only init once and only init if we have data
  if (this.svg || !this.data) {
    return false;
  }

  // Create the SVG
  this.elements.svg = d3
    .select(el)
    .append("svg")
    .attr("class", this.options.class)
    .attr("id", this.options.id)
    .attr("width", this.options.width)
    .attr("height", this.options.height);

  // Load the Precipitation chart if needed
  if (this.options.hasPrecipitation) {
    this.initPrecipitation(el);
  }

  if (this.options.hasTemperature) {
    this.initTemperature(el);
  }

  var chart = (this.elements.chart = this.elements.svg
    .append("g")
    .attr("class", "chart")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")"));

  this.elements.innerChart = this.elements.svg
    .append("g")
    .attr("class", "inner")
    .attr("clip-path", "url(#clip-" + this.options.id + ")")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")");

  this.elements.grid = {
    x: chart
      .append("g")
      .attr("class", "grid x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "grid y"),
  };

  this.elements.legendList = d3
    .select(el)
    .append("ul")
    .attr("class", "l-stacked legend has-left-spacing");
  this.elements.legendLine = this.elements.legendList.append("li").attr("class", "legend first");
  this.elements.legendLine.append("span").attr("class", "water line").html("&nbsp;");
  this.elements.legendLine
    .append("span")
    .attr("class", "content")
    .html(
      gettext("Development cumulative soil moisture in {{unit}}").replace(
        "{{unit}}",
        cropr.l10n.unit_precipitation_display
      )
    );

  if (!this.options.isBrush) {
    this.elements.legendFieldCapacity = this.elements.legendList
      .append("li")
      .attr("class", "legend first");
    this.elements.legendFieldCapacity
      .append("span")
      .attr("class", "field-capacity line")
      .html("&nbsp;");
    this.elements.legendFieldCapacity
      .append("span")
      .attr("class", "content")
      .html(
        gettext("Cumulative Field Capacity in {{unit}}").replace(
          "{{unit}}",
          cropr.l10n.unit_precipitation_display
        )
      );

    this.elements.legendWarningLine = this.elements.legendList
      .append("li")
      .attr("class", "legend first");
    this.elements.legendWarningLine
      .append("span")
      .attr("class", "warning-point line")
      .html("&nbsp;");
    this.elements.legendWarningLine
      .append("span")
      .attr("class", "content")
      .html(gettext("Warningpoint"));
  }

  if (this.options.hasPrecipitation) {
    this.elements.legendPrecipitation = this.elements.legendList
      .append("li")
      .attr("class", "legend");
    var precipitationSpan = this.elements.legendPrecipitation
      .append("span")
      .attr("class", "precipitation");
    precipitationSpan.append("span").attr("class", "precipitation1");
    precipitationSpan.append("span").attr("class", "precipitation2");
    precipitationSpan.append("span").attr("class", "precipitation3");

    this.elements.legendSpan = this.elements.legendPrecipitation
      .append("span")
      .attr("class", "content")
      .html(
        gettext("Precipitation in {{unit}} per {{interval}} hour")
          .replace("{{unit}}", cropr.l10n.unit_precipitation_display)
          .replace("{{interval}}", this.precipitationInterval)
      );
  }

  if (this.options.hasTemperature) {
    this.elements.legendLine = this.elements.legendList.append("li").attr("class", "legend first");
    this.elements.legendLine.append("span").attr("class", "temperature line").html("&nbsp;");
    this.elements.legendLine
      .append("span")
      .attr("class", "content")
      .html(
        gettext("Development temperature at {{depth}} {{unit}} depth")
          .replace("{{unit}}", cropr.l10n.unit_depth_display)
          .replace("{{depth}}", cropr.l10n_utils.depth_to_l10n(this.options.temperature_depth))
      );
  }

  if (!this.options.isBrush) {
    this.elements.legendBox = this.elements.legendList.append("li").attr("class", "legend");

    this.elements.boxes = this.elements.legendBox
      .selectAll(".depth")
      .data(this.scales.color.domain().slice().reverse())
      .enter()
      .append("span")
      .attr("class", "box")
      .style("background-color", this.scales.color)
      .html(function (d) {
        return cropr.l10n_utils.depth_to_l10n(d);
      });
    //      .html(function(d) { return d / 10; });
    var display_unit_distance_cm =
      cropr.l10n.unit_precipitation === "in" ? gettext("inches") : gettext("centimeters");
    this.elements.legendBox
      .append("span")
      .attr("class", "content")
      .html(
        gettext("Development soil moisture per depth in {{unit}}").replace(
          "{{unit}}",
          display_unit_distance_cm
        )
      );
  }

  this.elements.visuals = this.elements.innerChart
    .append("g")
    .attr("class", "visuals")
    .attr("mask", "url(#mask-" + this.options.id + ")");

  // Draw the stacked area chart
  this.elements.depths = this.elements.visuals
    .selectAll(".depth")
    .data(this.depthData)
    .enter()
    .append("g")
    .attr("class", "depth");

  this.elements.depthAreas = this.elements.depths
    .append("path")
    .attr("class", "area depth")
    .style("fill", function (d) {
      return _this.scales.color(d.depth);
    });

  if (!this.options.isBrush) {
    this.elements.fieldCapacityLine = this.elements.innerChart
      .append("line")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", this.chart.width)
      .attr("y2", 0)
      .attr("class", "line field-capacity");
    this.elements.warningLine = this.elements.innerChart
      .append("line")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", this.chart.width)
      .attr("y2", 0)
      .attr("class", "line warning-point");
  }

  // Draw the sum water line
  this.elements.line = this.elements.innerChart.append("path").attr("class", "line water");

  // Draw the today vertical line

  var brushSpacing = 8;
  if (this.options.isBrush) {
    brushSpacing = 12;
  }

  this.elements.chartCover = this.elements.svg
    .append("g")
    .attr("class", "cover")

    // .attr('clip-path', 'url(#clip-' + this.options.id + ')')
    .attr(
      "transform",
      "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
    );

  // Draw the mouse vertical line
  var clipId = "clip";
  if (this.options.hasPrecipitation || this.options.hasTemperature) {
    clipId = "clip-total";
  }

  //  this.elements.today = false;
  //  if (this.data.lastDataPoint.timestamp <= today) {
  this.elements.today = this.elements.chartCover
    .append("g")
    //     .attr('mask', 'none')
    .attr("class", "today")
    .attr(
      "transform",
      "translate(" +
        (this.scales.x(new Date(_this.data.lastDataPoint.timestamp)) - 2) +
        "," +
        0 +
        ")"
    );
  //     .attr('transform', 'translate(' + (this.scales.x(this.data.lastDataPoint.timestamp) - 2) + ',' + 0 + ')');

  this.elements.today
    .append("line")
    .attr("class", "line today")

    // 8 is the height of the line element to connect to the circle
    .attr("x1", 2)
    .attr("y1", 8 + brushSpacing)
    .attr("x2", 2)
    .attr("y2", this.totalChart.height);

  // @TODO: Vandaag boven de bol plaatsen
  this.elements.today
    .append("circle")
    .attr("class", "circle today")
    .attr("cx", 2)
    .attr("cy", 8 + brushSpacing)
    .attr("r", 6);
  // }

  // Draw the mouse vertical line
  this.elements.mouse = this.elements.chartCover
    .append("g")
    .attr("clip-path", "url(#" + clipId + "-" + this.options.id + ")")
    .append("g")
    .attr("class", "mouse")
    .style("opacity", "0");

  this.elements.mouse
    .append("line")
    .attr("class", "line mouse")
    .attr("x1", 2)
    .attr("y1", 8 + brushSpacing)
    .attr("x2", 2)
    .attr("y2", this.totalChart.height);

  this.elements.mouse
    .append("circle")
    .attr("class", "circle mouse")
    .attr("cx", 2)
    .attr("cy", 8 + brushSpacing)
    .attr("r", 6);

  // Draw the axis
  this.elements.axis = {
    x: chart
      .append("g")
      .attr("class", "axis x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "axis y"),
  };

  if (this.options.isBrush) {
    this.elements.brush = this.elements.svg
      .append("g")
      .attr("class", "brush")
      .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")");

    // Add the brush
    this._addBrush();
  }

  // Mouse rectacle
  if (!this.options.isBrush) {
    this.elements.tooltip = this.elements.mouse.append("g").attr("class", "dacom-online-tooltip");
    this.elements.tooltipBalloon = this.elements.tooltip.insert("rect").attr("rx", 2).attr("ry", 2);
    this.elements.tooltip1 = this.elements.tooltip
      .append("g")
      .attr("class", "dacom-online-tooltip");
    this.elements.tooltipText1 = this.elements.tooltip1.append("text").attr("class", "tooltipText");

    this.elements.tooltip2 = this.elements.tooltip
      .append("g")
      .attr("class", "dacom-online-tooltip");
    this.elements.tooltipText2 = this.elements.tooltip2.append("text").attr("class", "tooltipText");
    this.elements.tooltip3 = this.elements.tooltip
      .append("g")
      .attr("class", "dacom-online-tooltip");
    this.elements.tooltipText3 = this.elements.tooltip3.append("text").attr("class", "tooltipText");
    this.elements.tooltipPTAT = this.elements.tooltip
      .append("g")
      .attr("class", "dacom-online-tooltip");
    this.elements.tooltipTextPTAT = this.elements.tooltipPTAT
      .append("text")
      .attr("class", "tooltipText");

    this.elements.eventRect = this.elements.svg
      .append("svg:rect")
      .attr("class", "rect event")
      .attr("width", this.chart.width)
      .attr(
        "transform",
        "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
      )
      .attr("height", this.totalChart.height)
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .on("mouseout", function () {
        // on mouse out hide line, circles and text
        _this.dispatch(sensorActions.hovered(false));
      })
      .on("mouseover", function () {
        // on mouse in show line, circles and text
        _this.dispatch(sensorActions.hovered(true));
      })
      .on("mousemove", function () {
        // mouse moving over canvas
        //if(d3.event.which) return;
        var mouseX = d3.mouse(this)[0]; // mouse position in x
        var date = _this.scales.x.invert(mouseX); // date corresponding to mouse x
        _this.dispatch(sensorActions.hoverDate(date));
      })
      .on("click", function () {
        if (d3.event.defaultPrevented) return;
        _this.dispatch(sensorActions.togglePin());
      })
      .call(this.zoom)
      .on("wheel.zoom", null)
      .on("MozMousePixelScroll.zoom", null)
      .on("mousewheel.zoom", null)
      .on("dblclick.zoom", null);
  }

  // Draw the clippaths
  this._drawClipPaths();
  this.update();
};

// Set the data we need, we expect to receive it in the correct format
Chart.prototype.setData = function (data) {
  // Set the data
  this.data = extend(this.data, data);

  // Set min and max data value
  this.data.minDate = this.data.sensor.measurements.moisture[min(this.data.depths)][0].timestamp;
  this.data.maxDate =
    this.data.sensor.measurements.moisture[min(this.data.depths)][
      this.data.sensor.measurements.moisture[min(this.data.depths)].length - 1
    ].timestamp;

  // Create or update scales and axis etc.
  this._setScales();
  this._setAxis(); // @TODO: Do we need to update the axis after we reload data or is just the scales function enough?

  // Lower the data quality if we're showing the brush
  var moistureData = this.data.sensor.measurements.lowResCumulativeMoisture;
  if (!this.options.isBrush) {
    moistureData = this.data.sensor.measurements.cumulativeMoisture;
  }

  var stack = function (data) {
    data = sortBy(data, "depth");
    return data;
  };

  this.depthData = stack(
    this.scales.color.domain().map(function (depth) {
      return {
        depth: depth,
        values: moistureData[depth].map(function (d) {
          return { timestamp: d.timestamp, y: d.y, active: d.active };
        }),
      };
    })
  );

  this._setDataShapes(); // @TODO: This propbably doesn't need to happen at every update
};

Chart.prototype.updateHoverState = function (hovered, pinned, pinDate) {
  if (!this.options.isBrush && hovered && !pinned) {
    this.elements.mouse.style("opacity", ".5");
    this.elements.eventRect.classed("grab", false);
  } else if (!pinned) {
    this.elements.mouse.style("opacity", "0");
  } else {
    this.data.pinDate = pinDate;
    this.elements.mouse.style("opacity", "1");
    if (!this.options.isBrush) {
      this.elements.eventRect.classed("grab", true);
    } else {
      // Only for the brush
      this.updateHoverDate(pinDate, pinned);
      this.elements.mouse.attr(
        "transform",
        "translate(" + (this.scales.x(pinDate) - 2) + "," + 0 + ")"
      );
    }
  }

  this.data.pinned = pinned;
};

Chart.prototype.updateHoverDate = function (date, pinned) {
  var xPos = this.scales.x(date);
  var lines = 3;
  if (!pinned) {
    this.elements.mouse.attr("transform", "translate(" + (xPos - 2) + "," + 0 + ")");

    if (!this.options.isBrush) {
      var data = this.depthData;
      var values = data[0].values;
      var i = bisectDate(values, date, 1);
      var d0 = values[i - 1];
      var d1 = values[i];
      var d = d1 && date - d0.timestamp > d1.timestamp - date ? d1 : d0;
      var mm = d.y;
      var textPTAT = "";

      if (this.options.hasPrecipitation) {
        lines = 4;
        var datap = this.data.sensor.measurements.precipitation;
        i = bisectDate(datap, date, 1);
        d0 = datap[i - 1];
        d1 = datap[i];
        d = d1 && date - d0.timestamp > d1.timestamp - date ? d1 : d0;
        var precipitation = d.y;
        textPTAT =
          cropr.l10n_utils.mm_to_l10n(precipitation) + " " + cropr.l10n.unit_precipitation_display;
      }
      if (this.options.hasTemperature && this.options.hasPrecipitation) {
        textPTAT += " / ";
      }
      if (this.options.hasTemperature) {
        lines = 4;
        var datat = this.data.sensor.measurements.temperature[this.options.temperature_depth];
        i = bisectDate(datat, date, 1);
        d0 = datat[i - 1];
        d1 = datat[i];
        d = d1 && date - d0.timestamp > d1.timestamp - date ? d1 : d0;
        if (d) {
          var temperature = d.y;
          textPTAT +=
            cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(temperature), 1) +
            " " +
            cropr.l10n.unit_temp_display;
        } else {
          textPTAT += "- " + cropr.l10n.unit_temp_display;
        }
      }

      var dtool = new Date(date);
      var refill = this.data.sensor.fieldCapacity - mm;

      if (refill < 0) {
        refill = 0;
      }
      if (date) {
        dtool.setMinutes(Math.floor(date.getMinutes() / 15) * 15);
      }
      this.elements.tooltipText1.text(moment(dtool).format("L LT"));
      this.elements.tooltipText2.text(
        gettext("Available soil moisture") +
          ": " +
          cropr.l10n_utils.mm_to_l10n(mm) +
          " " +
          cropr.l10n.unit_precipitation_display
      );
      this.elements.tooltipText3.text(
        gettext("Refill capacity") +
          ": " +
          cropr.l10n_utils.mm_to_l10n(refill) +
          " " +
          cropr.l10n.unit_precipitation_display
      );
      this.elements.tooltipTextPTAT.text(textPTAT);

      // Calculate the bbox
      var tooltipMarginLeftRight = 5; // 5px margin
      var tooltipMarginTopBottom = 4; // 5px margin

      if (xPos < this.chart.width - this.chart.margin.right - 80) {
        this.elements.tooltip1.attr("transform", "translate(21, 15)");
        this.elements.tooltipText1.attr("text-anchor", "left");
        this.elements.tooltip2.attr("transform", "translate(21, 30)");
        this.elements.tooltipText2.attr("text-anchor", "left");
        this.elements.tooltip3.attr("transform", "translate(21, 45)");
        this.elements.tooltipText3.attr("text-anchor", "left");
        this.elements.tooltipPTAT.attr("transform", "translate(21, 60)");
        this.elements.tooltipTextPTAT.attr("text-anchor", "left");
      } else {
        this.elements.tooltip1.attr("transform", "translate(-20, 15)");
        this.elements.tooltipText1.attr("text-anchor", "end");
        this.elements.tooltip2.attr("transform", "translate(-20, 30)");
        this.elements.tooltipText2.attr("text-anchor", "end");
        this.elements.tooltip3.attr("transform", "translate(-20, 45)");
        this.elements.tooltipText3.attr("text-anchor", "end");
        this.elements.tooltipPTAT.attr("transform", "translate(-20, 60)");
        this.elements.tooltipTextPTAT.attr("text-anchor", "end");
      }

      var tooltips = [
        this.elements.tooltipText1,
        this.elements.tooltipText2,
        this.elements.tooltipText3,
        this.elements.tooltipTextPTAT,
      ];

      var bbox1 = this.elements.tooltipText1[0][0].getBBox();
      var bboxX = 0;
      var bboxWidth = 0;
      each(tooltips, function (tooltip, index) {
        var bbox = tooltip[0][0].getBBox();
        if (bbox.width > bboxWidth) {
          bboxWidth = bbox.width;
          bboxX = bbox.x;
        }
      });
      this.elements.tooltipBalloon
        .attr("y", bbox1.y - tooltipMarginTopBottom + 16)
        .attr("width", bboxWidth + 2 * tooltipMarginLeftRight)
        .attr("height", bbox1.height * lines + 2 * tooltipMarginTopBottom);

      if (xPos < this.chart.width - this.chart.margin.right - 80) {
        this.elements.tooltipBalloon.attr("x", bboxX - tooltipMarginLeftRight + 20);
      } else {
        this.elements.tooltipBalloon.attr("x", bboxX - tooltipMarginLeftRight - 20);
      }
    }
  }
};

// Update
Chart.prototype.update = function (data) {
  var _this = this;

  if (data) {
    // DIE SETDATA FUNCTIE BETER BEKIJKEN
    this.setData(data);
    this.zoom.x(this.scales.x);
    if (this.options.isBrush) {
      // Only adapt the viewfinder if the domain is smaller
      if (!(this.data.domain[0] <= this.data.minDate && this.data.domain[1] >= this.data.maxDate)) {
        this.brushFunction.x(this.scales.x).extent(this.data.domain);
        var brushFunction = this.brushFunction;
        this.viewFinder.call(brushFunction);
        this.brushFunction.extent(this.data.domain);
        this.viewFinder.transition().call(brushFunction);
      }
    }

    if (this.options.isBrush) {
      this.scales.x.domain(
        d3.extent(
          this.data.sensor.measurements.lowResCumulativeMoisture[min(this.data.depths)].map(
            function (d) {
              return d.timestamp;
            }
          )
        )
      );
    } else {
      this.scales.x.domain(data.domain);
    }

    // Update depth values
    this.elements.depthAreas.data(this.depthData);
  }

  var moistureData = this.data.sensor.measurements.lowResCumulativeMoisture;
  if (!this.options.isBrush) {
    moistureData = this.data.sensor.measurements.cumulativeMoisture;
    if (this.data.sensor.fieldCapacity) {
      var fieldCapacity = this.data.sensor.fieldCapacity;
      this.elements.fieldCapacityLine.attr(
        "transform",
        "translate(" + 0 + "," + this.scales.y(fieldCapacity) + ")"
      );
    }
    if (this.data.sensor.fieldCapacity && this.data.sensor.warningPercentage) {
      fieldCapacity = this.data.sensor.fieldCapacity;
      var warning = (fieldCapacity * (100 - this.data.sensor.warningPercentage)) / 100;
      this.elements.warningLine.attr(
        "transform",
        "translate(" + 0 + "," + this.scales.y(warning) + ")"
      );
    }
  }

  if (this.options.hasPrecipitation) {
    var compressedData = this._compressBatched(this.data.sensor.measurements.precipitation, true);
    var maxYDomain = d3.max(
      compressedData.map(function (d) {
        return d.y;
      })
    );

    if (!maxYDomain) maxYDomain = 5;
    this.scales.precipitationY.domain([0, maxYDomain]);

    // @TODO: IF PRECIPITATION
    var selection = this.elements.precipitationInnerChart
      .selectAll(".bar.stack")
      .data(compressedData);
    selection.enter().append("rect").attr("class", "precipitation bar stack");
    selection.exit().remove();

    this.elements.precipitationAxis.x.transition().call(this.axis.precipitationX);
    this.elements.precipitationAxis.y.transition().call(this.axis.precipitationY);
    this.elements.precipitationGrid.x.transition().call(this.axis.precipitationGridX);
    this.elements.precipitationGrid.y.transition().call(this.axis.precipitationGridY);

    selection
      .transition()
      .attr("x", function (d) {
        return _this.scales.x(d.timestamp);
      })
      .attr("y", function (d) {
        let y = _this.scales.precipitationY(d.y);
        return y;
      })
      .attr("height", function (d) {
        let y = _this.precipitationChart.height - _this.scales.precipitationY(d.y);
        return y;
      })
      .attr("width", 3);

    this.elements.legendSpan.html(
      gettext("Precipitation in {{unit}} per {{interval}} hour")
        .replace("{{unit}}", cropr.l10n.unit_precipitation_display)
        .replace("{{interval}}", this.precipitationInterval)
    );
  }

  if (this.options.hasTemperature) {
    this.defs.temperatureClip.attr("width", this.chart.width);
    this.elements.temperatureAxis.x.transition().call(this.axis.temperatureX);
    this.elements.temperatureAxis.y.transition().call(this.axis.temperatureY);
    this.elements.temperatureGrid.x.transition().call(this.axis.temperatureGridX);
    this.elements.temperatureGrid.y.transition().call(this.axis.temperatureGridY);

    this.elements.temperatureLine.datum(
      this.data.sensor.measurements.temperature[this.options.temperature_depth]
    );
    this.elements.temperatureLine.transition().attr("d", this.shapes.temperatureLine);
  }

  // Update the line graph data
  this.elements.line.datum(moistureData[min(this.data.depths)]);

  this.elements.axis.x.transition().call(this.axis.x);
  this.elements.axis.y.transition().call(this.axis.y);
  this.elements.grid.x.transition().call(this.axis.gridX);
  this.elements.grid.y.transition().call(this.axis.gridY);

  this.elements.depthAreas.transition().attr("d", function (d) {
    return _this.shapes.area(d.values);
  });

  this.elements.line.transition().attr("d", this.shapes.line);

  if (this.elements.today) {
    this.elements.today
      .transition()
      .attr(
        "transform",
        "translate(" +
          (this.scales.x(new Date(_this.data.lastDataPoint.timestamp)) - 2) +
          "," +
          0 +
          ")"
      );
    //    this.elements.today.transition().attr('transform', 'translate(' + (this.scales.x(this.data.lastDataPoint.timestamp) - 2) + ',' + 0 + ')');
  }

  each(this.data.sessions, function (session, i) {
    _this.elements.sessionsStart[i]
      .transition()
      .attr("transform", "translate(" + (_this.scales.x(session.startDate) - 2) + "," + 0 + ")");
    _this.elements.sessionsEnd[i]
      .transition()
      .attr("transform", "translate(" + (_this.scales.x(session.endDate) - 2) + "," + 0 + ")");
  });

  if (!this.options.isBrush && this.data.pinned) {
    this.elements.mouse
      .transition()
      .attr("transform", "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")");
  }

  // Update mask
  this.defs.mask
    .select(".mask.historic")
    .transition()
    .attr("width", _this.scales.x(_this.data.lastDataPoint.timestamp));

  this.defs.mask
    .select(".mask.forecast")
    .transition()
    .attr("x", _this.scales.x(_this.data.lastDataPoint.timestamp))
    .attr(
      "width",
      d3.max([0, this.chart.width - this.scales.x(this.data.lastDataPoint.timestamp)])
    );

  this.defs.clip.attr("width", this.chart.width);
  this.defs.totalClip.attr("width", this.chart.width);
};

// Not really DRY. The same as update besides the transitions
Chart.prototype._scroll = function () {
  var _this = this;
  this.elements.axis.x.call(this.axis.x);
  this.elements.grid.x.call(this.axis.gridX);

  this.elements.depthAreas.attr("d", function (d) {
    return _this.shapes.area(d.values);
  });

  this.elements.line.attr("d", this.shapes.line);
  if (this.elements.today) {
    this.elements.today.attr(
      "transform",
      "translate(" +
        (this.scales.x(new Date(_this.data.lastDataPoint.timestamp)) - 2) +
        "," +
        0 +
        ")"
    );
    //    this.elements.today.attr('transform', 'translate(' + (this.scales.x(this.data.lastDataPoint.timestamp) - 2) + ',' + 0 + ')');
  }

  each(this.data.sessions, function (session, i) {
    _this.elements.sessionsStart[i].attr(
      "transform",
      "translate(" + (_this.scales.x(session.startDate) - 2) + "," + 0 + ")"
    );
    _this.elements.sessionsEnd[i].attr(
      "transform",
      "translate(" + (_this.scales.x(session.endDate) - 2) + "," + 0 + ")"
    );
  });

  if (this.data.pinned) {
    this.elements.mouse.attr(
      "transform",
      "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")"
    );
  }

  this.defs.mask
    .select(".mask.historic")
    .attr("width", _this.scales.x(_this.data.lastDataPoint.timestamp));
  this.defs.mask
    .select(".mask.forecast")
    .attr("x", _this.scales.x(_this.data.lastDataPoint.timestamp))
    .attr(
      "width",
      d3.max([0, this.chart.width - this.scales.x(this.data.lastDataPoint.timestamp)])
    );

  if (this.options.hasPrecipitation) {
    this.elements.precipitationAxis.x.call(this.axis.precipitationX);
    this.elements.precipitationAxis.y.call(this.axis.precipitationY);
    this.elements.precipitationGrid.x.call(this.axis.precipitationGridX);
    this.elements.precipitationGrid.y.call(this.axis.precipitationGridY);

    this.elements.precipitationInnerChart
      .selectAll(".bar.stack")
      .attr("x", function (d) {
        return _this.scales.x(d.timestamp);
      })
      .attr("y", function (d) {
        return _this.scales.precipitationY(d.y);
      });
  }
};

Chart.prototype.resize = function (width, height) {
  if (width) {
    this.options.width = width;
    this.chart.width = this.options.width - (this.chart.margin.left + this.chart.margin.right);
  }

  if (height) {
    this.options.height = height;
    this.chart.width = this.options.height - (this.chart.margin.top + this.chart.margin.bottom);
  }

  this.elements.svg.style("width", this.options.width);
  this.elements.svg.style("height", this.options.height);

  // Update the scales
  this._setScales();
  this._setAxis();
  this.update();

  if (this.options.isBrush) {
    this.brushFunction.x(this.scales.x).extent(this.data.domain);
    var brushFunction = this.brushFunction;
    this.viewFinder.call(brushFunction);
  }

  // http://eyeseast.github.io/visible-data/2013/08/28/responsive-charts-with-d3/
};

Chart.prototype.destroy = function () {
  this.elements.svg.remove();
};

Chart.prototype._setScales = function () {
  var _this = this;
  var domain = this.data.domain;
  if (this.options.isBrush) {
    domain = d3.extent(
      this.data.sensor.measurements.lowResCumulativeMoisture[min(this.data.depths)].map(function (
        d
      ) {
        return d.timestamp;
      })
    );
  }

  var maxYDomain = 10;

  if (this.data.sensor.fieldCapacity) {
    maxYDomain = this.data.sensor.fieldCapacity * 1.15;
  }

  this.scales = {
    x: d3.time.scale().range([0, this.chart.width]).domain(domain),
    y: d3.scale.linear().range([this.chart.height, 0]).domain([0, maxYDomain]),

    color: d3.scale
      .ordinal()
      .range(this._getColorRange())
      .domain(sortBy(this.data.depths).reverse()),
  };

  if (this.options.hasPrecipitation) {
    this.scales.precipitationY = d3.scale.linear().range([this.precipitationChart.height, 0]);
  }

  if (this.options.hasTemperature) {
    var domainStart = this.data.domain[0];
    var domainStop = this.data.domain[1];

    var allTemperatures = [];
    each(this.data.sensor.measurements.temperature, function (values, temperature_depth) {
      // Store the indexes that are visible within the domain
      var visibleIndexes = [];

      // Push the actual values
      each(values, function (value, index) {
        // Change the y domain based on the code
        if (value.timestamp >= domainStart && value.timestamp <= domainStop) {
          visibleIndexes.push(index);
          allTemperatures.push(value.y);
        }
      });

      // Add one datum before to define the scale
      if (visibleIndexes[0] > 0) {
        allTemperatures.push(values[visibleIndexes[0] - 1].y);
      }

      if (visibleIndexes[visibleIndexes.length - 1] < values.length - 1) {
        allTemperatures.push(values[visibleIndexes[visibleIndexes.length - 1] + 1].y);
      }

      // If there are no measurements inside the period for the specific depth
      if (visibleIndexes.length === 0) {
        // Find the closest value
        var smallest = _this.data.minDate;
        var smallestKey = 0;
        var largest = _this.data.maxDate;
        var largestKey = values.length - 1;

        each(values, function (value, key) {
          if (value.timestamp > domainStop && value.timestamp < largest) {
            largest = value.timestamp;
            largestKey = key;
          }

          if (value.timestamp < domainStart && value.timestamp > smallest) {
            smallest = value.timestamp;
            smallestKey = key;
          }
        });

        if (values.length > 0) {
          allTemperatures.push(values[largestKey].y);
          allTemperatures.push(values[smallestKey].y);
        }
      }

      // If all temperatures are 0, add 10 to the list so we have a scale
      if (d3.max(allTemperatures) === 0) {
        allTemperatures.push(10);
      }
    });

    this.scales.temperatureY = d3.scale
      .linear()
      .range([this.temperatureChart.height, 0])
      .domain([d3.min(allTemperatures), d3.max(allTemperatures)]);
  }
};

Chart.prototype._setAxis = function () {
  var _this = this;

  // Calculate the amount of ticks
  var ticksY = Math.round(this.chart.height / 40);
  var ticksX = Math.round(this.chart.width / 150);

  this.axis = {
    x: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .outerTickSize(0)
      .tickFormat(timeFormat.chart),

    y: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return cropr.l10n_utils.mm_to_l10n(d) + " " + _this.options.i18n.unit;
      }),

    gridX: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .tickSize(-this.chart.height, 0, 0)
      .tickFormat(""),
    gridY: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY * 2)
      .tickSize(-this.chart.width, 0, 0)
      .tickFormat(""),
  };

  if (this.options.hasPrecipitation) {
    var ticksPrecipitationY = Math.round(this.precipitationChart.height / 40);

    this.axis.precipitationX = d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .outerTickSize(0)
      .tickFormat("");

    this.axis.precipitationY = d3.svg
      .axis()
      .scale(this.scales.precipitationY)
      .orient("left")
      .ticks(ticksPrecipitationY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return cropr.l10n_utils.mm_to_l10n(d) + " " + _this.options.i18n.unit;
      });

    this.axis.precipitationGridX = d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .tickSize(-this.precipitationChart.height, 0, 0)
      .tickFormat("");
    this.axis.precipitationGridY = d3.svg
      .axis()
      .scale(this.scales.precipitationY)
      .orient("left")
      .ticks(ticksPrecipitationY * 2)
      .tickSize(-this.precipitationChart.width, 0, 0)
      .tickFormat("");
  }

  if (this.options.hasTemperature) {
    var ticksTemperatureY = Math.round(this.temperatureChart.height / 15);

    this.axis.temperatureX = d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .outerTickSize(0)
      .tickFormat("");

    this.axis.temperatureY = d3.svg
      .axis()
      .scale(this.scales.temperatureY)
      .orient("left")
      .ticks(ticksTemperatureY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return (
          cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(d), 1) +
          " " +
          _this.options.i18n.unitTemperature
        );
      });

    this.axis.temperatureGridX = d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .tickSize(-this.temperatureChart.height, 0, 0)
      .tickFormat("");
    this.axis.temperatureGridY = d3.svg
      .axis()
      .scale(this.scales.temperatureY)
      .orient("left")
      .ticks(ticksTemperatureY)
      .tickSize(-this.temperatureChart.width, 0, 0)
      .tickFormat("");
  }
};

Chart.prototype._setDataShapes = function () {
  var _this = this;

  this.shapes = {
    area: d3.svg
      .area()
      .y0(function (d) {
        let y = _this.scales.y(0);
        return y;
      })
      .x(function (d) {
        return _this.scales.x(d.timestamp);
      })
      .y1(function (d) {
        let y = _this.scales.y(d.y);
        return y;
      }),

    line: d3.svg
      .line()
      .x(function (d) {
        return _this.scales.x(d.timestamp);
      })
      .y(function (d) {
        let y = _this.scales.y(d.y);
        return y;
      }),
  };
  if (this.options.hasTemperature) {
    this.shapes.temperatureLine = d3.svg
      .line()
      .x(function (d) {
        return _this.scales.x(d.timestamp);
      })
      .y(function (d) {
        let y = _this.scales.temperatureY(d.y);
        return y;
      });
  }
};

Chart.prototype._getColorRange = function () {
  // Return a color range based on

  switch (this.data.depths.length) {
    case 1:
      return ["#6dc4aa"];
    case 2:
      return ["#3a9077", "#9dd7c6"];
    case 3:
      return ["#317863", "#55ba9c", "#9dd7c6"];
    case 4:
      return ["#27604f", "#44a98b", "#85ceb8", "#ceebe2"];
    case 5:
      return ["#27604f", "#3a9077", "#55ba9c", "#85ceb8", "#b5e1d4"];
    case 6:
      return ["#27604f", "#3a9077", "#55ba9c", "#85ceb8", "#b5e1d4", "#e6f5f0"];
    case 7:
      return ["#3a9077", "#44a98b", "#55ba9c", "#6dc4aa", "#85ceb8", "#9dd7c6", "#b5e1d4"];
    case 8:
      return [
        "#317863",
        "#3a9077",
        "#44a98b",
        "#55ba9c",
        "#6dc4aa",
        "#85ceb8",
        "#9dd7c6",
        "#b5e1d4",
      ];
    case 9:
      return [
        "#317863",
        "#3a9077",
        "#44a98b",
        "#55ba9c",
        "#6dc4aa",
        "#85ceb8",
        "#9dd7c6",
        "#b5e1d4",
        "#ceebe2",
      ];
    case 10:
      return [
        "#27604f",
        "#317863",
        "#3a9077",
        "#44a98b",
        "#55ba9c",
        "#6dc4aa",
        "#85ceb8",
        "#9dd7c6",
        "#b5e1d4",
        "#ceebe2",
      ];
    case 11:
      return [
        "#27604f",
        "#317863",
        "#3a9077",
        "#44a98b",
        "#55ba9c",
        "#6dc4aa",
        "#85ceb8",
        "#9dd7c6",
        "#b5e1d4",
        "#ceebe2",
        "#e6f5f0",
      ];
    case 12:
      return [
        "#1d483b",
        "#27604f",
        "#317863",
        "#3a9077",
        "#44a98b",
        "#55ba9c",
        "#6dc4aa",
        "#85ceb8",
        "#9dd7c6",
        "#b5e1d4",
        "#ceebe2",
        "#e6f5f0",
      ];
    default:
      return null;
  }
};

Chart.prototype._addBrush = function () {
  var _this = this;
  var tooltipMarginLeftRight = 5; // 5px margin
  var tooltipMarginTopBottom = 4; // 5px margin

  // Brush function
  var brushFunction;
  this.brushFunction = brushFunction = d3.svg
    .brush()
    .x(this.scales.x)
    .extent(this.data.domain)
    .on("brushend", _brushend)
    .on("brushstart", _brushStart)
    .on("brush", _brushing);

  var viewFinder;
  this.viewFinder = viewFinder = this.elements.brush
    .append("g")
    .attr("class", "x brush")
    .call(brushFunction);

  // Paint the view finder
  var draggerSize = 24; // The width and height of the dragger

  viewFinder
    .selectAll("rect")
    .attr("y", -1)
    .attr("height", this.chart.height + 2);

  viewFinder
    .selectAll(".resize.e")
    .append("rect")
    .attr("transform", "translate(-1," + (this.chart.height / 2 - draggerSize / 2) + ")")
    .attr("class", "dragger")
    .attr("width", draggerSize - 1)
    .attr("height", draggerSize)
    .attr("rx", 2)
    .attr("ry", 2);

  viewFinder
    .selectAll(".resize.w")
    .append("rect")
    .attr(
      "transform",
      "translate(-" + (draggerSize - 2) + "," + (this.chart.height / 2 - draggerSize / 2) + ")"
    )
    .attr("class", "dragger")
    .attr("width", draggerSize - 1)
    .attr("height", draggerSize)
    .attr("rx", 2)
    .attr("ry", 2);

  // Add the viewfinder data
  var tooltip = viewFinder.append("g").attr("class", "tooltip is-hidden");

  var tooltipText = tooltip
    .append("text")
    .attr("class", "tooltipText")
    .attr("text-anchor", "middle");

  var tooltipBalloon = tooltip.insert("rect", "text.tooltipText").attr("rx", 2).attr("ry", 2);

  // Set the tooltip of the brush
  //setBrushTooltip();

  var draggerWest = viewFinder
    .selectAll(".resize.w")
    .append("g")
    .attr("class", "draggerlines")
    .attr(
      "transform",
      "translate(-" + (draggerSize + 1) + "," + (this.chart.height / 2 - draggerSize / 2) + ")"
    );
  draggerWest
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 9)
    .attr("width", 2)
    .attr("height", draggerSize - 13);
  draggerWest
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 13)
    .attr("width", 2)
    .attr("height", draggerSize - 13);
  draggerWest
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 17)
    .attr("width", 2)
    .attr("height", draggerSize - 13);

  var draggerEast = viewFinder
    .selectAll(".resize.e")
    .append("g")
    .attr("class", "draggerlines")
    .attr("transform", "translate(-1," + (this.chart.height / 2 - draggerSize / 2) + ")");
  draggerEast
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 7)
    .attr("width", 2)
    .attr("height", draggerSize - 13);
  draggerEast
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 11)
    .attr("width", 2)
    .attr("height", draggerSize - 13);
  draggerEast
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 15)
    .attr("width", 2)
    .attr("height", draggerSize - 13);

  function _brushend() {
    var brush = brushFunction.empty() ? _this.scales.x.domain() : brushFunction.extent();
    tooltip.attr("class", "tooltip is-hidden");

    // Apply brush action
    _this.dispatch(sensorActions.brushDomain(brush));
  }

  function _brushStart() {
    tooltip.attr("class", "dacom-online-tooltip");
    setBrushTooltip();
  }

  function _brushing() {
    setBrushTooltip();

    //@TODO add date to brush
  }

  function setBrushTooltip() {
    var brush = brushFunction.empty() ? _this.scales.x.domain() : brushFunction.extent();

    var brushStartPosition = _this.scales.x(brush[0]);
    var brushStopPosition = _this.scales.x(brush[1]);
    var tooltipCenter = brushStartPosition + (brushStopPosition - brushStartPosition) / 2;

    var dateText = moment(brush[0]).format("dd, LL") + " — " + moment(brush[1]).format("dd, LL");
    dateText = dateText.toUpperCase();
    tooltipText.text(dateText);

    // Calculate the border box
    var bbox = tooltipText[0][0].getBBox();
    var bboxWidth = bbox.width + 2 * tooltipMarginLeftRight;
    tooltipBalloon
      .attr("x", bbox.x - tooltipMarginLeftRight)
      .attr("y", bbox.y - tooltipMarginTopBottom)
      .attr("width", bboxWidth)
      .attr("height", bbox.height + 2 * tooltipMarginTopBottom);

    var brushWidth = _this.elements.visuals[0][0].getBBox().width;
    tooltipCenter = Math.max(bboxWidth / 2, tooltipCenter);
    tooltipCenter = Math.min(brushWidth - bboxWidth / 2, tooltipCenter);
    tooltip.attr("transform", "translate(" + tooltipCenter + "," + (_this.chart.height + 22) + ")");
  }
};

Chart.prototype._compressBatched = function (data, redraw) {
  // Interval in hours
  // To be calulated
  var interval = 1; // Data is per kwartier, 4 intervals is een uur
  var barWidth = 3;

  // How many bars are going to fit insite the current chart
  var fittingBars = this.chart.width / barWidth; // Was: +1 om ruimte tussen de bars te houden

  // How many hours is our current domain
  var domainStart = moment(this.data.domain[0]);
  var domainEnd = moment(this.data.domain[1]);

  var diff = domainEnd.diff(domainStart, "hours");

  while (fittingBars * interval < diff) {
    interval++;
  }

  if (interval > 12) {
    interval = 24;
  } else if (interval > 6) {
    interval = 12;
  } else if (interval > 4) {
    interval = 6;
  }

  // Only recalculate if neccesary
  if (this.precipitationInterval === interval && !redraw) {
    return this.data.compressedData;
  }

  this.precipitationInterval = interval;

  var compressedData = [];
  var sum = 0;
  var maxTime = false;

  each(data, function (value) {
    var time = value.timestamp;
    if (!maxTime) {
      maxTime = new Date(time);
      var correction = time.getHours() % interval;
      maxTime.setMinutes(0);
      if (interval === 1) {
        maxTime.setTime(maxTime.getTime());
      } else {
        maxTime.setTime(maxTime.getTime() + (interval - correction) * 3600 * 1000);
      }
    }

    if (maxTime <= time) {
      // Measures are created at the end of the interval, so change the time of the measurement to half of the interval
      var averageTime = new Date(maxTime);
      averageTime.setTime(maxTime.getTime() - (interval * 3600 * 1000) / 2);

      compressedData.push({
        timestamp: averageTime,
        y: sum,
      });

      sum = 0;

      maxTime = false;
    }

    // @TODO: before adding the value to the sum, check if the measurement is active
    sum += Number(value.y);
  });

  // The last push, with the remaining rain fall
  if (maxTime) {
    // Measures are created at the end of the interval, so change the time of the measurement to half of the interval
    var averageTime = new Date(maxTime);
    averageTime.setTime(maxTime.getTime() - (interval * 3600 * 1000) / 2);

    compressedData.push({
      timestamp: averageTime,
      y: sum,
    });
  }

  this.data.compressedData = compressedData;
  return compressedData;
};

Chart.prototype._drawClipPaths = function () {
  var _this = this;
  var defs;
  this.defs = defs = this.elements.svg.append("defs");

  this.defs.clip = defs
    .append("svg:clipPath")
    .attr("id", "clip-" + this.options.id)
    .append("svg:rect")
    .attr("x", 0)
    .attr("y", -2)
    .attr("height", this.chart.height + 8);

  this.defs.totalClip = defs
    .append("svg:clipPath")
    .attr("id", "clip-total-" + this.options.id)
    .append("svg:rect")
    .attr("x", "0")
    .attr("y", "-2")
    .attr("height", this.totalChart.height + 8);

  var pattern = defs
    .append("svg:pattern")
    .attr("id", "strokes-" + this.options.id)
    .attr("patternUnits", "userSpaceOnUse")
    .attr("width", 10)
    .attr("height", 10);
  pattern.append("rect").attr("width", 10).attr("height", 10).attr("fill", "#eee");
  pattern
    .append("path")
    .attr("d", "M-1,1 l2,-2M0,10 l10,-10M9,11 l2,-2")
    .attr("stroke", "#999")
    .attr("stroke-width", 3);

  this.defs.mask = defs
    .append("svg:mask")
    .attr("id", "mask-" + this.options.id)
    .attr("width", this.chart.width)
    .attr("height", this.chart.height + 8)
    .attr("x", 0)
    .attr("y", -2);

  this.defs.mask
    .append("svg:rect")
    .attr("class", "mask historic")
    .attr("height", _this.chart.height)
    .attr("fill", "white");

  this.defs.mask
    .append("svg:rect")
    .attr("class", "mask forecast")
    .attr("height", _this.chart.height)
    .attr("fill", "url(#strokes-" + _this.options.id + ")");

  if (this.options.hasTemperature) {
    this.defs.temperatureClip = defs
      .append("svg:clipPath")
      .attr("id", "clip-temperature-" + this.options.id)
      .append("svg:rect")
      .attr("x", "0")
      .attr("y", "-8")
      .attr("height", this.temperatureChart.height + 8);
  }
};

export default Chart;
